import { FaroTextField } from "@components/common/faro-text-field/faro-text-field";
import { AccessLevel } from "@components/access-level/access-level";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { SdbProject } from "@custom-types/project-types";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { Skeleton } from "@mui/material";
import { ProjectAddress } from "@pages/project-details/project-overview/project-address";
import { appStateSelector } from "@store/app/app-selector";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { DEFAULT_INPUT_FONT_SIZE } from "@styles/common-styles";
import {
  DEFAULT_PROJECT_AREA,
  getProjectArea,
  getProjectSize,
} from "@utils/project-utils";
import { ChangeProjectDetailsEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { DASH } from "@utils/ui-utils";
import { useMemo } from "react";

interface Props {
  /** Whether the project is editable or not */
  isProjectEditable: boolean;

  /** The project to display in the ProjectOverview */
  project?: SdbProject | null;

  /** Whether the project is still loading and skeleton should be shown instead */
  isLoading: boolean;
}

/** Default maximum rows for the address text field. */
const ADDRESS_DEFAULT_MAX_ROWS = 5;

/**
 * The number of rows for the skeleton.
 * It should match the number of rows in the component, currently we have client, address, access level and area.
 */
const SKELETON_ROWS = 4;

/**
 * Shows the secondary details for a project. These include the client name, address and area.
 * They are displayed below the primary details of the project.
 */
export function SecondaryProjectDetails({
  isProjectEditable,
  project,
  isLoading,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { canChangeProjectAccessLevel } =
    useHasUserValidPermissionProjectLevel();
  const { handleErrorWithToast } = useErrorContext();
  const { trackEvent } = useTrackEvent();
  const { isAlphaTestingEnabled } = useAppSelector(appStateSelector);

  /** Only show the project area if the user typed something different than the default one. */
  const shouldShowProjectArea: boolean = useMemo(() => {
    return !!project && project.area?.amount !== DEFAULT_PROJECT_AREA;
  }, [project]);

  /**
   * Updates the project details in the backend and returns the new value if successful.
   * Otherwise, returns the old value.
   *
   * @param value The new value to update the project details with.
   * @param field The field to update.
   */
  async function onUpdateProjectDetail(
    value: string,
    field: "address" | "clientName"
  ): Promise<string> {
    if (!project) {
      return "";
    }
    try {
      await dispatch(
        updateProjectDetails({
          payload: { [field]: value },
        })
      );
      return value;
    } catch (error) {
      handleErrorWithToast({
        id: `changeProjectDetails-${field}-${Date.now().toString()}`,
        title: `Could not change ${field}`,
        error,
      });
      return project[field] ?? "";
    }
  }

  if (isLoading || !project) {
    return (
      <>
        {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            height="48px"
            sx={{ marginTop: "30px" }}
          />
        ))}
      </>
    );
  }

  return (
    <>
      <FaroTextField
        label="Client"
        initialValue={project.clientName ?? ""}
        defaultEmptyValue={DASH}
        sx={{ mb: "30px" }}
        isReadOnly={!isProjectEditable}
        onConfirmed={(value) => {
          trackEvent({
            name: ChangeProjectDetailsEvents.changeProjectClient,
          });

          return onUpdateProjectDetail(value, "clientName");
        }}
      />

      {/* TODO: Remove feature flag and only keep the ProjectAddress block -> https://faro01.atlassian.net/browse/ST-3944 */}
      {isAlphaTestingEnabled ? (
        <ProjectAddress
          project={project}
          isProjectEditable={isProjectEditable}
        />
      ) : (
        <FaroTextField
          label="Address"
          initialValue={project.address}
          defaultEmptyValue={DASH}
          sx={{ mb: "30px" }}
          isReadOnly={!isProjectEditable}
          isMultiline={true}
          maxRows={ADDRESS_DEFAULT_MAX_ROWS}
          fontSize={DEFAULT_INPUT_FONT_SIZE}
          onConfirmed={(value) => {
            trackEvent({
              name: ChangeProjectDetailsEvents.changeProjectAddress,
            });

            return onUpdateProjectDetail(value, "address");
          }}
        />
      )}

      <AccessLevel
        isAccessLevelEditable={canChangeProjectAccessLevel && isProjectEditable}
        type="project"
        entity={project}
        location="secondary-project-details"
        sx={{ mb: "30px" }}
      />

      {shouldShowProjectArea && (
        <FaroTextField
          label="Area"
          initialValue={getProjectArea(project)}
          sx={{ mb: "30px" }}
        />
      )}

      <FaroTextField
        label="Size"
        initialValue={getProjectSize(project)}
        sx={{ mb: "30px" }}
      />
    </>
  );
}
